import {
    ApiShopperLandingPageLink,
    isApiFullLandingPageLink,
} from '@storefronts/common-types'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { getLandingPageLinkForProduct, getLandingPageLinks } from '../../api'
import {
    buildLandingPageLinkHashMap,
    mergeLandingPageLink,
    resolveOrderedLinksAgainstMap,
    type LandingPageLinkHashMapInputs,
} from '../utils/resolveLandingPageLinksByHash'
import { getLandingPageLinksFromProductHistory } from '../utils/userEventTools'
import { UserEventDataContext } from './UserEventDataContextProvider'
import { ViddyLPContext } from './viddyLPContext'

export type CachedOrderedLinksForHashes = {
    orderedLinks: ApiShopperLandingPageLink[]
    /** When true, every requested hash is already in session cache, history, or the landing page payload — no API fetch required. */
    allHashesResolved: boolean
}

export type LandingPageLinksContextValue = {
    /** Static + product history + dynamically fetched links. Use for cart conversion and "look through" consumers. */
    resolvedLandingPageLinks: ApiShopperLandingPageLink[]
    /** Sync lookup: returns link from static or cache, or null. */
    getLinkForProduct: (productId: string) => ApiShopperLandingPageLink | null
    /** Fetches link for product, caches it, and returns. Resolves once per product per session. */
    fetchAndCacheLinkForProduct: (
        productId: string,
    ) => Promise<ApiShopperLandingPageLink | null>
    /**
     * Fetches Full links for hashes that are missing or still CARD, caches them, and returns.
     * Sync cache reads (getCachedOrderedLinksForHashes) can still return CARD for PLP/cards.
     */
    fetchAndCacheLinksForHashes: (
        linkHashes: string[],
    ) => Promise<ApiShopperLandingPageLink[]>
    /**
     * Sync snapshot of ordered links for the given hashes using the same merge rules as fetchAndCacheLinksForHashes.
     * Use for SSR and to show partial rows before fetch completes.
     */
    getCachedOrderedLinksForHashes: (
        linkHashes: string[],
    ) => CachedOrderedLinksForHashes
}

const defaultValue: LandingPageLinksContextValue = {
    resolvedLandingPageLinks: [],
    getLinkForProduct: () => null,
    fetchAndCacheLinkForProduct: () => Promise.resolve(null),
    fetchAndCacheLinksForHashes: () => Promise.resolve([]),
    getCachedOrderedLinksForHashes: () => ({
        orderedLinks: [],
        allHashesResolved: true,
    }),
}

export const LandingPageLinksContext =
    React.createContext<LandingPageLinksContextValue>(defaultValue)

export const LandingPageLinksProvider: React.FC<{
    children: React.ReactNode
    /** Products pre-fetched by admin editor for dynamic grid previews */
    dynamicGridProducts?: ApiShopperLandingPageLink[]
}> = ({ children, dynamicGridProducts }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { abandonmentStorefrontData } = useContext(UserEventDataContext)
    const [dynamicLinksMap, setDynamicLinksMap] = useState(
        () => new Map<string, ApiShopperLandingPageLink>(),
    )
    const inFlightProductIdsRef = useRef<
        Map<string, Promise<ApiShopperLandingPageLink | null>>
    >(new Map())
    const inFlightHashesRef = useRef<
        Map<string, Promise<ApiShopperLandingPageLink[]>>
    >(new Map())

    useEffect(() => {
        const inFlightProductIdsMap = inFlightProductIdsRef.current
        const inFlightHashesMap = inFlightHashesRef.current
        return () => {
            inFlightProductIdsMap.clear()
            inFlightHashesMap.clear()
        }
    }, [])

    const staticLinks = useMemo(
        () => [
            ...(landingPage?.landingPageLinks ?? []),
            ...(landingPage?.sectionLandingPageLinks ?? []),
            ...(landingPage?.cartUpsells ?? []),
            ...(dynamicGridProducts ?? []),
        ],
        [
            landingPage?.landingPageLinks,
            landingPage?.sectionLandingPageLinks,
            landingPage?.cartUpsells,
            dynamicGridProducts,
        ],
    )

    const productHistoryLinks = useMemo(
        () => getLandingPageLinksFromProductHistory(abandonmentStorefrontData),
        [abandonmentStorefrontData],
    )

    const getLinkForProduct = useCallback(
        (productId: string): ApiShopperLandingPageLink | null => {
            const fromStatic = staticLinks.find(
                (link) => link.shopifyId === productId,
            )
            if (fromStatic) return fromStatic
            const fromHistory = productHistoryLinks.find(
                (link) => link.shopifyId === productId,
            )
            if (fromHistory) return fromHistory
            return (
                Array.from(dynamicLinksMap.values()).find(
                    (link) => link.shopifyId === productId,
                ) ?? null
            )
        },
        [staticLinks, productHistoryLinks, dynamicLinksMap],
    )

    const fetchAndCacheLinkForProduct = useCallback(
        async (
            productId: string,
        ): Promise<ApiShopperLandingPageLink | null> => {
            const cached = getLinkForProduct(productId)
            if (cached) return cached

            const inFlight = inFlightProductIdsRef.current.get(productId)
            if (inFlight) return inFlight

            const client = landingPage?.client
            if (!client) return null

            const promise = getLandingPageLinkForProduct(
                client.clientHash,
                productId,
            )
                .then((link) => {
                    inFlightProductIdsRef.current.delete(productId)
                    if (!!link) {
                        setDynamicLinksMap((prev) => {
                            const next = new Map(prev)
                            next.set(link.linkHash, link)
                            return next
                        })
                    } else {
                        console.warn(
                            `Could not fetch landing page link for product: ${productId}`,
                        )
                    }
                    return link
                })
                .catch(() => {
                    console.warn(
                        `Failed to fetch landing page link for product: ${productId}`,
                    )
                    inFlightProductIdsRef.current.delete(productId)
                    return null
                })
            inFlightProductIdsRef.current.set(productId, promise)
            return promise
        },
        [landingPage?.client, getLinkForProduct],
    )

    const linkHashMapInputs = useMemo(
        (): LandingPageLinkHashMapInputs => ({
            sessionCachedLinks: dynamicLinksMap,
            productHistoryLinks,
            staticLandingPageLinks: staticLinks,
        }),
        [dynamicLinksMap, productHistoryLinks, staticLinks],
    )

    const getCachedOrderedLinksForHashes = useCallback(
        (linkHashes: string[]): CachedOrderedLinksForHashes => {
            const hashToLink = buildLandingPageLinkHashMap(linkHashMapInputs)
            const { orderedKnownLinks, missingHashes, validHashes } =
                resolveOrderedLinksAgainstMap(linkHashes, hashToLink)
            return {
                orderedLinks: orderedKnownLinks,
                allHashesResolved:
                    validHashes.length === 0 || missingHashes.length === 0,
            }
        },
        [linkHashMapInputs],
    )

    const fetchAndCacheLinksForHashes = useCallback(
        async (linkHashes: string[]): Promise<ApiShopperLandingPageLink[]> => {
            const hashToLink = buildLandingPageLinkHashMap(linkHashMapInputs)
            const { validHashes } = resolveOrderedLinksAgainstMap(
                linkHashes,
                hashToLink,
            )

            if (validHashes.length === 0) return []

            const hashesToFetch = validHashes.filter((hash) => {
                const cached = hashToLink.get(hash)
                return cached == null || !isApiFullLandingPageLink(cached)
            })

            const resolveKnown = (
                fetchedByHash?: Map<string, ApiShopperLandingPageLink>,
            ): ApiShopperLandingPageLink[] =>
                validHashes
                    .map((h) => {
                        const cached = hashToLink.get(h)
                        const fetched = fetchedByHash?.get(h)
                        if (cached && fetched) {
                            return mergeLandingPageLink(cached, fetched)
                        }
                        return fetched ?? cached
                    })
                    .filter((l): l is ApiShopperLandingPageLink => l != null)

            if (hashesToFetch.length === 0) {
                return resolveKnown()
            }

            const batchKey = `${landingPage?.landingPageHash ?? ''}:${[...hashesToFetch].sort().join(',')}`
            const inFlight = inFlightHashesRef.current.get(batchKey)
            if (inFlight) {
                const fetched = await inFlight
                return resolveKnown(
                    new Map(fetched.map((l) => [l.linkHash, l])),
                )
            }

            const promise = getLandingPageLinks(
                hashesToFetch,
                landingPage?.landingPageHash,
            )
                .then((fetched) => {
                    inFlightHashesRef.current.delete(batchKey)
                    if (fetched.length > 0) {
                        setDynamicLinksMap((prev) => {
                            const next = new Map(prev)
                            fetched.forEach((link) =>
                                next.set(
                                    link.linkHash,
                                    mergeLandingPageLink(
                                        next.get(link.linkHash),
                                        link,
                                    ),
                                ),
                            )
                            return next
                        })
                    }
                    return fetched
                })
                .catch((error) => {
                    console.warn(
                        `Failed to fetch landing page links for hashes: ${hashesToFetch.join(', ')}`,
                        error,
                    )
                    inFlightHashesRef.current.delete(batchKey)
                    return []
                })

            inFlightHashesRef.current.set(batchKey, promise)
            const fetched = await promise
            return resolveKnown(new Map(fetched.map((l) => [l.linkHash, l])))
        },
        [linkHashMapInputs, landingPage?.landingPageHash],
    )

    const resolvedLandingPageLinks = useMemo(() => {
        const byLinkHash = new Map<string, ApiShopperLandingPageLink>()
        const setLink = (link: ApiShopperLandingPageLink) => {
            byLinkHash.set(
                link.linkHash,
                mergeLandingPageLink(byLinkHash.get(link.linkHash), link),
            )
        }
        dynamicLinksMap.forEach(setLink)
        productHistoryLinks.forEach(setLink)
        staticLinks.forEach(setLink)
        return Array.from(byLinkHash.values())
    }, [staticLinks, productHistoryLinks, dynamicLinksMap])

    const value: LandingPageLinksContextValue = useMemo(
        () => ({
            resolvedLandingPageLinks,
            getLinkForProduct,
            fetchAndCacheLinkForProduct,
            fetchAndCacheLinksForHashes,
            getCachedOrderedLinksForHashes,
        }),
        [
            resolvedLandingPageLinks,
            getLinkForProduct,
            fetchAndCacheLinkForProduct,
            fetchAndCacheLinksForHashes,
            getCachedOrderedLinksForHashes,
        ],
    )

    return (
        <LandingPageLinksContext.Provider value={value}>
            {children}
        </LandingPageLinksContext.Provider>
    )
}
